/**
 * Thin API client.
 *
 * In development Vite proxies /api to the API server, so the browser stays on
 * one origin and CORS behaves the same as it will behind App Service. In
 * production VITE_API_BASE_URL points at the deployed API.
 */
export const BASE = import.meta.env.VITE_API_BASE_URL || "/api";

const TOKEN_KEY = "trustcare.token";

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const setToken = (token: string): void => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = (): void => localStorage.removeItem(TOKEN_KEY);

export class ApiError extends Error {
    status: number;
    body: unknown;

    constructor(status: number, message: string, body: unknown) {
        super(message);
        this.status = status;
        this.body = body;
    }
}

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
    const token = getToken();
    const headers = new Headers(init.headers);
    headers.set("Content-Type", "application/json");
    if (token) {
        headers.set("Authorization", `Bearer ${token}`);
    }

    const response = await fetch(`${BASE}${path}`, { ...init, headers });

    if (response.status === 401) {
        // The token is gone or the account was deactivated server-side. Drop it
        // so the app returns to the sign-in screen rather than looping on 401s.
        clearToken();
        window.dispatchEvent(new CustomEvent("trustcare:signed-out"));
        throw new ApiError(401, "Your session has ended. Please sign in again.", null);
    }

    if (response.status === 204) {
        return undefined as T;
    }

    const body = await response.json().catch(() => null);

    if (!response.ok) {
        const message =
            (body && typeof body === "object" && "error" in body && String(body.error)) ||
            `Request failed (${response.status})`;
        throw new ApiError(response.status, message, body);
    }

    return body as T;
};

export const api = {
    get: <T>(path: string) => request<T>(path),
    post: <T>(path: string, body: unknown) =>
        request<T>(path, { method: "POST", body: JSON.stringify(body) }),
    put: <T>(path: string, body: unknown) =>
        request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
    delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
